import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShieldOff, ShieldCheck, LogIn, Settings, AlertTriangle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { formatBRL } from '../../lib/currency';
import { useToast } from '../../components/ui/ToastSystem';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';

interface UserProfile {
  id: string; email: string; full_name: string; subscription_tier: string;
  created_at: string; banned_at: string | null; ban_reason: string | null;
  unlimited_access: boolean | null; subscription_expires_at: string | null;
  birth_date: string | null; sun_sign: string | null;
}
interface Payment {
  id: string; amount: number; product_price_id: string; status: string;
  created_at: string; credits_expected: number | null;
}
interface UserDetail {
  profile: UserProfile;
  payments: Payment[];
  credits: { questions: number; synastry: number; astromatch: number };
}
interface ActivityRow {
  feature: string; label: string; count: number; last_accessed: string;
  total_cost_usd: number; cache_hits: number;
}

const TIER_COLORS: Record<string, string> = {
  free: 'var(--text-muted)', monthly: '#60a5fa', annual: '#F5C842', astrologer_pro: '#a78bfa',
};
const TIER_LABELS: Record<string, string> = {
  free: 'Grátis', monthly: 'Mensal', annual: 'Anual', astrologer_pro: 'Astrólogo Pro',
};

export function AdminUserDetailPage() {
  const { id: userId } = useParams<{ id: string }>();
  const navigate       = useNavigate();
  const { profile: adminProfile } = useAuth();
  const { showToast } = useToast();

  const [detail,          setDetail]          = useState<UserDetail | null>(null);
  const [activity,        setActivity]        = useState<ActivityRow[]>([]);
  const [notes,           setNotes]           = useState<{ id: string; note: string; created_at: string }[]>([]);
  const [loading,         setLoading]         = useState(true);
  const [loadingActivity] = useState(false);
  const [newNote,         setNewNote]         = useState('');
  const [addingNote,      setAddingNote]      = useState(false);

  // Ban modal
  const [banModal,  setBanModal]  = useState(false);
  const [banReason, setBanReason] = useState('');
  const [banning,   setBanning]   = useState(false);

  // Impersonate modal
  const [impModal,  setImpModal]  = useState(false);
  const [impReason, setImpReason] = useState('');
  const [imping,    setImping]    = useState(false);

  // Grant credits modal
  const [grantModal, setGrantModal] = useState(false);
  const [grantType,  setGrantType]  = useState<'questions' | 'synastry' | 'astromatch'>('questions');
  const [grantAmt,   setGrantAmt]   = useState('');
  const [grantNote,  setGrantNote]  = useState('');
  const [granting,   setGranting]   = useState(false);

  // Access modal
  const [accessModal,  setAccessModal]  = useState(false);
  const [accessTier,   setAccessTier]   = useState<'free' | 'monthly' | 'annual'>('free');
  const [accessUnlim,  setAccessUnlim]  = useState(false);
  const [accessExpiry, setAccessExpiry] = useState('');
  const [savingAccess, setSavingAccess] = useState(false);

  useEffect(() => {
    if (!userId) return;
    load();
  }, [userId]);

  async function load() {
    setLoading(true);
    const [{ data: ud }, actData] = await Promise.all([
      supabase.functions.invoke('admin-get-user', { body: { userId } }),
      supabase.functions.invoke('admin-get-user-activity', { body: { userId } }),
    ]);
    if (ud?.data) setDetail(ud.data);
    if (actData?.data?.data?.activity) setActivity(actData.data.data.activity);
    setLoading(false);
    loadNotes();
  }

  async function loadNotes() {
    const { data } = await supabase
      .from('admin_user_notes')
      .select('id, note, created_at')
      .eq('user_id', userId!)
      .order('created_at', { ascending: false });
    setNotes(data ?? []);
  }

  async function addNote() {
    if (!newNote.trim() || !adminProfile?.id) return;
    setAddingNote(true);
    await supabase.from('admin_user_notes').insert({
      user_id: userId, admin_id: adminProfile.id, note: newNote.trim(),
    });
    setNewNote('');
    setAddingNote(false);
    loadNotes();
  }

  async function banUser() {
    if (!banReason.trim()) return;
    setBanning(true);
    const { data } = await supabase.functions.invoke('admin-ban-user', {
      body: { userId, reason: banReason.trim() },
    });
    setBanning(false);
    if (data?.ok) {
      showToast({ type: 'success', message: `Usuário banido.` });
      setBanModal(false); setBanReason('');
      load();
    } else {
      showToast({ type: 'error', message: data?.error ?? 'Erro ao banir' });
    }
  }

  async function unbanUser() {
    if (!window.confirm('Desbanir este usuário?')) return;
    const { data } = await supabase.functions.invoke('admin-unban-user', { body: { userId } });
    if (data?.ok) { showToast({ type: 'success', message: 'Usuário desbanido.' }); load(); }
    else showToast({ type: 'error', message: data?.error ?? 'Erro ao desbanir' });
  }

  async function confirmImpersonation() {
    if (impReason.trim().length < 5) return;
    setImping(true);
    const { data, error } = await supabase.functions.invoke('admin-impersonate', {
      body: { targetUserId: userId, reason: impReason.trim() },
    });
    setImping(false);
    if (error) { showToast({ type: 'error', message: error.message }); return; }
    if (data?.data?.token_hash) {
      const sess = await supabase.auth.getSession();
      const tok = sess.data.session?.access_token ?? '';
      if (tok) {
        try {
          sessionStorage.setItem('__admin_impersonate_restore', JSON.stringify({
            access_token: tok,
            refresh_token: sess.data.session?.refresh_token ?? '',
            email: adminProfile?.email ?? '',
          }));
        } catch { /* ignore */ }
      }
      const { error: se } = await supabase.auth.verifyOtp({ token_hash: data.data.token_hash, type: 'magiclink' });
      if (!se) { showToast({ type: 'success', message: `Impersonando` }); window.location.href = '/dashboard'; }
      else { sessionStorage.removeItem('__admin_impersonate_restore'); showToast({ type: 'error', message: se.message }); }
    } else {
      showToast({ type: 'error', message: data?.error ?? 'Erro ao impersonar' });
    }
  }

  async function grantCredits() {
    if (!grantAmt || grantNote.length < 5) return;
    setGranting(true);
    await supabase.functions.invoke('admin-grant-credits', {
      body: { userId, creditType: grantType, amount: parseInt(grantAmt), motivo: grantNote },
    });
    setGranting(false); setGrantModal(false); setGrantAmt(''); setGrantNote('');
    load();
  }

  function openAccessModal() {
    if (!detail) return;
    setAccessTier((detail.profile.subscription_tier as 'free' | 'monthly' | 'annual') ?? 'free');
    setAccessUnlim(detail.profile.unlimited_access ?? false);
    setAccessExpiry(detail.profile.subscription_expires_at ? detail.profile.subscription_expires_at.slice(0, 10) : '');
    setAccessModal(true);
  }

  async function saveAccess() {
    if (!detail) return;
    setSavingAccess(true);
    const { error } = await supabase.from('profiles').update({
      subscription_tier:       accessTier,
      unlimited_access:        accessUnlim,
      subscription_expires_at: accessExpiry ? new Date(accessExpiry).toISOString() : null,
      updated_at:              new Date().toISOString(),
    }).eq('id', detail.profile.id);
    setSavingAccess(false);
    if (error) showToast({ type: 'error', message: 'Erro ao atualizar acesso' });
    else { showToast({ type: 'success', message: 'Acesso atualizado ✦' }); setAccessModal(false); load(); }
  }

  async function deleteAccount() {
    if (!window.confirm('Excluir esta conta? Esta ação é irreversível (LGPD Art. 18).')) return;
    await supabase.functions.invoke('delete-account', { body: { userId } });
    navigate('/admin-ap/users');
  }

  if (loading) return (
    <div>
      <button className="btn btn-ghost btn-sm" style={{ marginBottom: '1rem' }} onClick={() => navigate(-1)}>← Voltar</button>
      <div className="card shimmer" style={{ height: '300px' }} />
    </div>
  );

  if (!detail) return (
    <div>
      <button className="btn btn-ghost btn-sm" style={{ marginBottom: '1rem' }} onClick={() => navigate(-1)}>← Voltar</button>
      <p style={{ color: 'var(--text-muted)' }}>Usuário não encontrado.</p>
    </div>
  );

  const { profile, payments, credits } = detail;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/admin-ap/users')}>← Usuários</button>
      </div>

      <AdminPageHeader
        title={profile.full_name ?? 'Usuário'}
        objective={`Detalhes completos de ${profile.email}`}
      />

      {/* Profile card */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            <p className="font-display" style={{ fontSize: '1.25rem', color: 'var(--text-primary)', margin: '0 0 0.2rem' }}>{profile.full_name ?? 'Sem nome'}</p>
            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', margin: 0 }}>{profile.email}</p>
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: '0.25rem 0 0' }}>
              <span style={{ color: TIER_COLORS[profile.subscription_tier] }}>{TIER_LABELS[profile.subscription_tier] ?? profile.subscription_tier}</span>
              {profile.sun_sign ? ` · ${profile.sun_sign}` : ''}
              {profile.birth_date ? ` · Nasc. ${new Date(profile.birth_date).toLocaleDateString('pt-BR')}` : ''}
              {profile.subscription_expires_at ? ` · Expira ${new Date(profile.subscription_expires_at).toLocaleDateString('pt-BR')}` : ''}
              {' · membro desde '}{new Date(profile.created_at).toLocaleDateString('pt-BR')}
            </p>
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: '0.2rem 0 0', fontFamily: 'monospace' }}>ID: {profile.id}</p>
            {profile.banned_at && (
              <p style={{ color: '#f87171', fontSize: 'var(--text-caption)', margin: '0.35rem 0 0', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                <ShieldOff size={12} /> Banido em {new Date(profile.banned_at).toLocaleDateString('pt-BR')}
                {profile.ban_reason ? ` · ${profile.ban_reason}` : ''}
              </p>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
              onClick={() => { setImpModal(true); setImpReason(''); }}>
              <LogIn size={14} /> Impersonar
            </button>
            <button className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }} onClick={openAccessModal}>
              <Settings size={14} /> Acesso
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => setGrantModal(true)}>+ Créditos</button>
            {profile.banned_at ? (
              <button className="btn btn-secondary btn-sm" style={{ color: '#4ade80', display: 'flex', alignItems: 'center', gap: '0.4rem' }} onClick={unbanUser}>
                <ShieldCheck size={14} /> Desbanir
              </button>
            ) : (
              <button className="btn btn-secondary btn-sm" style={{ color: '#fbbf24', display: 'flex', alignItems: 'center', gap: '0.4rem' }} onClick={() => setBanModal(true)}>
                <ShieldOff size={14} /> Banir
              </button>
            )}
            <button className="btn btn-danger btn-sm" onClick={deleteAccount}>Excluir conta</button>
          </div>
        </div>
      </div>

      {/* Credits */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem' }}>Créditos</p>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          {Object.entries(credits).map(([type, bal]) => (
            <div key={type} style={{ textAlign: 'center', flex: '1 1 80px' }}>
              <p style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--petrol)', fontFamily: 'var(--font-display)', margin: '0 0 0.2rem' }}>{bal}</p>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: 0, textTransform: 'capitalize' }}>{type}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Activity */}
      <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: '1rem' }}>
        <div style={{ padding: '1rem', borderBottom: '1px solid var(--border-subtle)' }}>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>
            Serviços acessados
          </p>
        </div>
        {loadingActivity ? (
          <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>Carregando…</div>
        ) : activity.length === 0 ? (
          <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic', fontSize: 'var(--text-caption)' }}>
            Nenhum serviço registrado ainda.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Serviço</th>
                  <th style={{ textAlign: 'right' }}>Acessos</th>
                  <th style={{ textAlign: 'right' }}>Cache hits</th>
                  <th style={{ textAlign: 'right' }}>Custo (USD)</th>
                  <th>Último acesso</th>
                </tr>
              </thead>
              <tbody>
                {activity.map(a => (
                  <tr key={a.feature}>
                    <td style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)' }}>
                      {a.label || a.feature}
                      {a.label && a.label !== a.feature && (
                        <span style={{ display: 'block', fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'monospace', fontWeight: 400 }}>{a.feature}</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', color: 'var(--text-secondary)', fontSize: 'var(--text-caption)' }}>{a.count}</td>
                    <td style={{ textAlign: 'right', color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>{a.cache_hits}</td>
                    <td style={{ textAlign: 'right', color: a.total_cost_usd > 0 ? '#fbbf24' : 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>
                      {a.total_cost_usd > 0 ? `$${a.total_cost_usd.toFixed(4)}` : '-'}
                    </td>
                    <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {new Date(a.last_accessed).toLocaleString('pt-BR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Notes */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem' }}>
          Notas internas
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          <input type="text" className="form-input" value={newNote} onChange={e => setNewNote(e.target.value)}
            placeholder="Adicionar nota..." style={{ flex: 1 }}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addNote(); } }} />
          <button className="btn btn-primary btn-sm" onClick={addNote} disabled={addingNote || newNote.trim().length < 2}>
            {addingNote ? '...' : 'Adicionar'}
          </button>
        </div>
        {notes.length === 0 ? (
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', fontStyle: 'italic' }}>Nenhuma nota ainda.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {notes.map(n => (
              <div key={n.id} style={{ padding: '0.6rem 0.75rem', backgroundColor: 'var(--bg-base)', borderRadius: 'var(--radius-sm)', borderLeft: '2px solid var(--petrol)' }}>
                <p style={{ margin: '0 0 0.25rem', fontSize: 'var(--text-label)', color: 'var(--text-primary)' }}>{n.note}</p>
                <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>{new Date(n.created_at).toLocaleString('pt-BR')}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Payments */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '1rem', borderBottom: '1px solid var(--border-subtle)' }}>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>Pagamentos</p>
        </div>
        <table className="data-table" style={{ width: '100%' }}>
          <thead><tr><th>Data</th><th>Produto</th><th>Valor</th><th>Status</th><th>Créditos</th></tr></thead>
          <tbody>
            {payments.map(p => (
              <tr key={p.id}>
                <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{new Date(p.created_at).toLocaleDateString('pt-BR')}</td>
                <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>{p.product_price_id}</td>
                <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{formatBRL(p.amount)}</td>
                <td>
                  <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '999px',
                    backgroundColor: p.status === 'succeeded' ? 'rgba(74,222,128,0.12)' : 'rgba(239,68,68,0.1)',
                    color: p.status === 'succeeded' ? '#4ade80' : '#fca5a5' }}>
                    {p.status}
                  </span>
                </td>
                <td>
                  {p.credits_expected !== null && p.status === 'succeeded' ? (
                    <span style={{ fontSize: '11px', color: '#4ade80' }}>✓ Concedido</span>
                  ) : p.credits_expected === null ? (
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>-</span>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <AlertTriangle size={12} color="#fbbf24" />
                      <span style={{ fontSize: '11px', color: '#fbbf24' }}>Não concedido</span>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {payments.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic', padding: '1.5rem' }}>Nenhum pagamento</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Ban modal */}
      {banModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="card" style={{ maxWidth: '380px', width: '100%', border: '1px solid rgba(239,68,68,0.3)' }}>
            <h2 style={{ fontSize: '1rem', color: '#f87171', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <ShieldOff size={16} /> Banir usuário
            </h2>
            <div className="form-group">
              <label className="form-label">Motivo (obrigatório)</label>
              <textarea className="form-input" value={banReason} onChange={e => setBanReason(e.target.value)}
                rows={3} style={{ resize: 'vertical', minHeight: '80px' }} />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-danger btn-sm" onClick={banUser} disabled={banning || banReason.trim().length < 5}>
                {banning ? 'Banindo…' : 'Confirmar'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => { setBanModal(false); setBanReason(''); }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Impersonate modal */}
      {impModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="card" style={{ maxWidth: '400px', width: '100%', border: '1px solid rgba(245,200,66,0.3)' }}>
            <h2 style={{ fontSize: '1rem', color: 'var(--petrol)', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <LogIn size={16} /> Impersonar usuário
            </h2>
            <div className="form-group">
              <label className="form-label">Motivo (obrigatório, mín. 5 caracteres)</label>
              <textarea className="form-input" value={impReason} onChange={e => setImpReason(e.target.value)}
                rows={3} style={{ resize: 'vertical', minHeight: '80px' }} maxLength={500} />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-primary btn-sm" onClick={confirmImpersonation} disabled={imping || impReason.trim().length < 5}>
                {imping ? 'Iniciando…' : 'Confirmar'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => { setImpModal(false); setImpReason(''); }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Grant credits modal */}
      {grantModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="card" style={{ maxWidth: '380px', width: '100%' }}>
            <h2 style={{ fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '0.75rem' }}>Conceder créditos</h2>
            <div className="form-group">
              <label className="form-label">Tipo</label>
              <select className="form-select" value={grantType} onChange={e => setGrantType(e.target.value as typeof grantType)}>
                <option value="questions">Perguntas</option>
                <option value="synastry">Sinastria</option>
                <option value="astromatch">AstroMatch</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Quantidade</label>
              <input type="number" className="form-input" value={grantAmt} onChange={e => setGrantAmt(e.target.value)} min="1" max="100" />
            </div>
            <div className="form-group">
              <label className="form-label">Motivo (obrigatório)</label>
              <input type="text" className="form-input" value={grantNote} onChange={e => setGrantNote(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-primary btn-sm" onClick={grantCredits} disabled={granting || !grantAmt || grantNote.length < 5}>
                {granting ? 'Concedendo…' : 'Conceder'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setGrantModal(false)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Set access modal */}
      {accessModal && detail && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="card" style={{ maxWidth: '380px', width: '100%' }}>
            <h2 style={{ fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <Settings size={16} /> Alterar acesso
            </h2>
            <div className="form-group">
              <label className="form-label">Plano</label>
              <select className="form-select" value={accessTier} onChange={e => setAccessTier(e.target.value as typeof accessTier)}>
                <option value="free">Grátis</option>
                <option value="monthly">Mensal</option>
                <option value="annual">Anual</option>
              </select>
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input type="checkbox" id="accessUnlim2" checked={accessUnlim} onChange={e => setAccessUnlim(e.target.checked)} />
              <label htmlFor="accessUnlim2" className="form-label" style={{ margin: 0 }}>Acesso ilimitado ∞</label>
            </div>
            <div className="form-group">
              <label className="form-label">Data de expiração (opcional)</label>
              <input type="date" className="form-input" value={accessExpiry} onChange={e => setAccessExpiry(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-primary btn-sm" onClick={saveAccess} disabled={savingAccess}>
                {savingAccess ? 'Salvando…' : 'Salvar'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setAccessModal(false)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
